import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";

const CardMarket = lazy(() => import("@/components/games/CardMarket").then((m) => ({ default: m.CardMarket })));
const AchievementWall = lazy(() => import("@/components/games/AchievementWall").then((m) => ({ default: m.AchievementWall })));

const TITLE = "Palworld TCG Card Flipping Game — Market Flip";
const DESCRIPTION =
  "Free Palworld card flipping game: start on 1,000 virtual gold, trade 20 real card lots across 20 market days and try to double your bankroll.";
const OG_IMAGE = "https://palstars.com/og/palworld-games.jpg";

const STEPS = [
  { name: "Open the market", text: "Twenty real Palworld card lots are rotated fresh for every new game, priced from the Pal Stars Index, and you start on 1,000 virtual gold." },
  { name: "Buy the dips", text: "Buy copies of any lot while its ask is low; your gold and holdings update instantly." },
  { name: "Advance the day", text: "Each market day moves every lot on its own random walk with occasional shocks, so the next move cannot be predicted from the last — sell into the spikes." },
  { name: "Close the market", text: "After twenty days your holdings liquidate at the final asks and your bankroll is scored against the 1,000 gold you started with." },
];

const FAQ: FaqItem[] = [
  { q: "What is the Palworld card flipping game?", a: "Card Market Flip is a free Palworld TCG trading simulator: 20 real card lots, 20 market days and 1,000 virtual gold. Buy low, sell high and finish above your starting bankroll." },
  { q: "Is the gold real or worth anything?", a: "No. Market gold is virtual, has no cash value, cannot be bought and never touches your Pal Stars pack or Palsino balance — the game is a sandbox for practising price judgement." },
  { q: "How are the card prices set?", a: "Opening asks come from the Pal Stars Index baseline for each printed card code, then every market day applies a per-lot drift, a random swing and an occasional shock, so no pattern repeats." },
  { q: "Do the card lots change between games?", a: "Yes. All 20 lots are rotated at random each time you open the market, so a new run never trades the same basket as the last one." },
  { q: "Does the flipping game reflect real Palworld card prices?", a: "The starting values do, because they use the same rarity-and-set baseline as the price hub. The daily swings are simulated, so treat the game as practice, not a forecast." },
  { q: "Are there limits on the market game?", a: "Yes — 15 market runs per browser per UTC day, and badges for finishing in profit or doubling your gold. Nothing else is at stake." },
];

export const Route = createFileRoute("/card-market")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld Card Market Flip — TCG trading sim" },
      { property: "og:description", content: "Twenty market days, 20 real card lots, 1,000 virtual gold — can you double it?" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/card-market" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/card-market" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld Games", "/palworld-games"], ["Card Market Flip", "/card-market"]]),
      toolSchema({ name: "Palworld Card Market Flip", description: DESCRIPTION, path: "/card-market" }),
      howToSchema({ name: "How to play the Palworld card flipping game", description: "Open the market, buy dips, advance market days and close in profit.", path: "/card-market", steps: STEPS }),
      faqSchema(FAQ, "https://palstars.com/card-market"),
    ],
  }),
  component: CardMarketPage,
});

function CardMarketPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars Arcade · TCG game</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld card market flip</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          A free Palworld TCG card flipping game: start on 1,000 virtual gold, trade 20 real card lots across 20
          market days and try to double the bankroll. Gold is virtual and never touches your account balance.
        </p>
      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Palworld TCG card market flip</h2><p className="mt-2 text-sm text-muted-foreground">Loading the market…</p></section>}>
        <CardMarket />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How the market works</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} id={`step-${i + 1}`} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Achievement badges</h2><p className="mt-2 text-sm text-muted-foreground">Loading badges…</p></section>}>
        <AchievementWall game="card-market" heading="Market badges" />
      </Suspense>

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">Play next</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/palworld-games" className="font-semibold text-primary hover:underline">All free Palworld games →</Link></li>
          <li><Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG price chart →</Link> real marketplace medians.</li>
          <li><Link to="/price-guess" className="font-semibold text-primary hover:underline">Palworld card price quiz →</Link></li>
          <li><Link to="/trades" className="font-semibold text-primary hover:underline">Card trading →</Link> swap real pulls with members.</li>
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld card flipping game FAQ" />
    </div>
  );
}
